export type ScoreLabel = { min: number; max: number; label: string, description: string, emoji?: string };

export const scoreLabels: ScoreLabel[] = [
  { min: 0, max: 14, label: "Clean", description: "No visible bias or manipulative language. Rare, objective reporting." },
  { min: 15, max: 29, label: "Minor Spin", description: "Slight framing or word choice that subtly nudges perception." },
  { min: 30, max: 44, label: "Noticeable Bias", description: "Clear preference in tone or perspective, though not overtly deceptive." },
  { min: 45, max: 59, label: "Heavy Framing", description: "Strong selective emphasis, emotionally charged phrasing, or loaded narrative." },
  { min: 60, max: 74, label: "Strong Manipulation", description: "The content uses emotional language and framing to influence the reader." },
  { min: 75, max: 89, label: "Propaganda-Level Tactics", description: "Intentional use of propaganda techniques to influence reader opinion or emotion." },
  { min: 90, max: 100, label: "Extreme Propaganda", description: "Highly deceptive content with the goal of persuasion, division, or disinformation." },
];

export const shortScoreLabels: ScoreLabel[] = [
  { min: 0, max: 19, emoji: "✅", label: "Very Low", description: "Minimal to no visible bias or manipulation. Reporting is largely neutral and objective." },
  { min: 20, max: 39, emoji: "⚠️", label: "Low", description: "Slight bias or framing detected. Some language or emphasis nudges perception subtly." },
  { min: 40, max: 59, emoji: "⚠️", label: "Moderate", description: "Noticeable bias and framing present, but not strongly manipulative or deceptive." },
  { min: 60, max: 79, emoji: "🚨", label: "High", description: "Significant use of emotional language, selective framing, or persuasion techniques." },
  { min: 80, max: 100, emoji: "🧨", label: "Very High", description: "Strong propaganda tactics detected with clear intent to manipulate or mislead." },
];

export function getScoreLabel(score: number, short = false): ScoreLabel {
  const labels = short ? shortScoreLabels : scoreLabels;
  const match = labels.find(({ min, max }) => score >= min && score <= max);
  return match ? match : { label: "Unknown", description: "No description available", min: 0, max: 100 };
}

const techniqueMap: { [key: string]: { emoji: string, slug: string } } = {
  "appeal to authority": {
    emoji: "👨‍⚖️ ", slug: "appeal-to-authority"
  },
  "appeal to fear/prejudice": {
    emoji: "😱 ", slug: "appeal-to-fear-prejudice"
  },
  "black-and-white fallacy/dictatorship": {
    emoji: "⚫⚪ ", slug: "black-and-white-fallacy"
  },
  "causal oversimplification": {
    emoji: "🧩 ", slug: "causal-oversimplification"
  },
  "doubt": {
    emoji: "🤔 ", slug: "doubt"
  },
  "exaggeration/minimisation": {
    emoji: "📢 ", slug: "exaggeration-minimisation"
  },
  "flag-waving": {
    emoji: "🚩 ", slug: "flag-waving"
  },
  "glittering generalities (virtue)": {
    emoji: "✨ ", slug: "glittering-generalities"
  },
  "loaded language": {
    emoji: "💣 ", slug: "loaded-language"
  },
  "misrepresentation of someone's position (straw man)": {
    emoji: "🪆 ", slug: "straw-man"
  },
  "name calling/labeling": {
    emoji: "🏷️ ", slug: "name-calling-labeling"
  },
  "obfuscation, intentional vagueness, confusion": {
    emoji: "🌫️ ", slug: "obfuscation-vagueness-confusion"
  },
  "presenting irrelevant data (red herring)": {
    emoji: "🐟 ", slug: "red-herring"
  },
  "reductio ad hitlerum": {
    emoji: "🧟‍♂️ ", slug: "reductio-ad-hitlerum"
  },
  "repetition": {
    emoji: "🔁 ", slug: "repetition"
  },
  "slogans": {
    emoji: "📣 ", slug: "slogans"
  },
  "smears": {
    emoji: "🧼❌ ", slug: "smears"
  },
  "thought-terminating cliché": {
    emoji: "🛑 ", slug: "thought-terminating-cliche"
  },
  "whataboutism": {
    emoji: "🔄 ", slug: "whataboutism"
  },
  "bandwagon": {
    emoji: "🧑‍🤝‍🧑 ", slug: "bandwagon"
  },
}

export function getTechniqueEmoji(name: string) {
  return techniqueMap[name.toLowerCase()]?.emoji || "🫥";
}

export function getTechniqueUrl(name: string) {
  const slug = techniqueMap[name.toLowerCase()]?.slug;
  if (!slug) {
    return '';
  }
  return `/techniques/${slug}`;
}

export function getScoredEntityName(scoredEntity: string) {
  const scoredEntitiesMap: { [key: string]: string } = {
    content_author: "Content Author",
    quoted_speaker: "Quoted Speaker",
  }
  return scoredEntitiesMap[scoredEntity] || scoredEntity;
}
